type GoogleCredentialResponse = { credential: string };
type GoogleCredentialCallback = (response: GoogleCredentialResponse) => void | Promise<void>;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: Record<string, unknown>) => void;
          renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let initializedClientId: string | null = null;
let activeCallback: GoogleCredentialCallback | null = null;

function loadGoogleIdentity(): Promise<void> {
  if (window.google) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    const script = existing || document.createElement('script');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Google sign-in could not be loaded.')), { once: true });
    if (!existing) {
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

export async function renderGoogleIdentityButton(
  element: HTMLElement,
  clientId: string,
  callback: GoogleCredentialCallback,
  text: 'signin_with' | 'signup_with',
) {
  await loadGoogleIdentity();
  if (!window.google) throw new Error('Google sign-in is unavailable.');

  activeCallback = callback;
  if (!initializedClientId) {
    window.google.accounts.id.initialize({
      client_id: clientId,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response: GoogleCredentialResponse) => activeCallback?.(response),
    });
    initializedClientId = clientId;
  } else if (initializedClientId !== clientId) {
    throw new Error('Google sign-in client configuration changed unexpectedly.');
  }

  element.innerHTML = '';
  window.google.accounts.id.renderButton(element, {
    type: 'standard', theme: 'outline', size: 'large', text, shape: 'rectangular', width: 448,
  });

  return () => {
    if (activeCallback === callback) activeCallback = null;
  };
}
